import { fetchAccount } from "@/services/account.queries";
import { fetchPurchasePass } from "@/services/purchasepass.queries";
import { useToastNotify } from "@/stores/toastStore";
import { UNKNOWN_VALUE } from "@/utils/etc";
import { getValueFromStorage, saveToStorage } from "@/utils/secureStore";
import { useIsFocused } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import { t } from "@transifex/native";
import { useRouter } from "expo-router";
import { PurchasePassFailReason } from "ipctg-inflow-types/InFlowEnums";
import type { ParkPurchasePassPOSTSuccessResponse } from "ipctg-inflow-types/InFlowParkAPI";
import { useState } from "react";
import { useRefreshOnFocus } from "./useRefreshOnFocus";

interface LastPurchasePass {
  purchasePassResponse: ParkPurchasePassPOSTSuccessResponse;
  termsAndConditionsOKTimestamp: string;
}

export function usePurchasePass(enabled: boolean, termsAndConditionsOKTimestamp: string, stripeSessionId: string | undefined, rateID: string | null) {
  /*
    There is an underlying bug where just calling notify
    causes an infinite loop. Likely to do something with
    how the toasts are populated. In the meantime we are
    papering over the bug with this state.
  */
  const [isErrorNotified, setErrorNotified] = useState(false);
  const { notify } = useToastNotify();
  const router = useRouter();
  const isFocused = useIsFocused();

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ["purchase-pass", termsAndConditionsOKTimestamp, stripeSessionId],
    enabled: enabled && !!rateID && isFocused,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const idToken = await getValueFromStorage("idToken");
      const storedLastPurchasePass = await getValueFromStorage("lastPurchasePass");

      const storedLastPurchasePassInstance = storedLastPurchasePass ? (JSON.parse(storedLastPurchasePass) as LastPurchasePass) : null;

      if (storedLastPurchasePassInstance?.termsAndConditionsOKTimestamp === termsAndConditionsOKTimestamp) {
        return storedLastPurchasePassInstance.purchasePassResponse;
      }

      const account = await fetchAccount({ cognitoIDToken: idToken || "" });
      const rsp = await fetchPurchasePass({
        cognitoIDToken: idToken ?? "",
        stripeCheckoutSessionID: stripeSessionId ?? null,
        rateProductID: rateID as string,
        termsAndConditionsOKTimestamp,
        phoneNumber: account.phone || UNKNOWN_VALUE,
        // Jake wants to send only phone for now
        // email: user?.email,
      });

      await saveToStorage(
        "lastPurchasePass",
        JSON.stringify({
          purchasePassResponse: rsp,
          termsAndConditionsOKTimestamp,
        }),
      );

      return rsp;
    },
  });

  useRefreshOnFocus(refetch, enabled && !!rateID && isFocused);

  if (isFetching) {
    return { data: undefined, isPending: true };
  }

  if (error && !isErrorNotified) {
    if ("failReason" in error) {
      switch (error.failReason) {
        case PurchasePassFailReason.PaymentFailed: {
          notify({
            severity: "error",
            message: t("Payment failed. Please check your payment details."),
          });
          break;
        }
        case PurchasePassFailReason.SystemError: {
          notify({
            severity: "error",
            message: t("An unexpected system error occurred. Please try again later."),
          });
          router.replace("/welcome");
          break;
        }
      }
    } else {
      notify({
        severity: "error",
        message: t("An unknown error occurred. Please try again."),
      });
    }

    console.error("Error fetching purchase pass data:", error);
    setErrorNotified(true);
    return { data: undefined, isPending: false };
  }

  return {
    data,
    isPending: false,
  };
}
